import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IReportingDetail } from 'src/app/interfaces/ireporting-detail';
import { LearnerListModalComponent } from '../learner-list-modal/learner-list-modal.component';
import { Router } from '@angular/router';
import { TooltipService } from 'src/app/services/general/tooltip.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';

@Component({
  selector: 'app-practice-assignments',
  templateUrl: './practice-assignments.component.html',
  styleUrls: ['./practice-assignments.component.scss'],
  standalone: false
})
export class PracticeAssignmentsComponent {
  @Output() loadPracticeAssignments: EventEmitter<void> = new EventEmitter();
  @Input() practiceLiveData: IReportingDetail | undefined;
  @Input() practiceOldData: IReportingDetail | undefined;
  @Input() headerText: string = '';
  @Input() summaryText: string = '';
  @Input() moduleAssignmentCount: number | undefined;
  reportData: IReportingDetail | undefined;

  header: string = '';
  summary: string = '';
  content: string = '';
  showOldAssignments: boolean = true;
  ctaText: string = ''
  ctaImg = "/assets/icons/arrow/chevron-down-dark-blue.svg"
  toggleString: string = '';
  remainingAssignments: number = 0;
  oldDataLoading: boolean = false;
  sortField: string = "";
  sortAscending: boolean = true;
  sortIcon: string = "/assets/icons/arrow/arrow-narrow-up.svg";
  translationLoaded: boolean = false;

  constructor(private dialog: MatDialog,
    private router: Router,
    private tooltipService: TooltipService,
    private amplitudeService: AmplitudeService,
    private translocoService: TranslocoService,
    private translationService: TranslationService
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['practiceOldData']) {
      this.oldDataLoading = false;
      this.practiceOldData?.moduleAssignmentsDetail.forEach(assignment => {
        assignment.oldData = true;
        assignment.visible = true;
      });

      if (this.practiceLiveData && this.practiceOldData && this.practiceLiveData.moduleAssignmentsDetail && this.practiceOldData.moduleAssignmentsDetail) {
        // Combine the moduleAssignmentsDetail arrays from both liveData and oldData
        const combinedAssignments = [
          ...this.practiceLiveData.moduleAssignmentsDetail,
          ...this.practiceOldData.moduleAssignmentsDetail
        ];
        this.reportData = {
          ...this.practiceLiveData,
          moduleAssignmentsDetail: combinedAssignments
        };
      }

    }

  }


  ngOnInit() {
    this.translationService.loadScopedTranslation('reporting').then(() => {
      this.ctaText = this.translocoService.translate('SEE_PRACTICE_ASSIGNMENT', {}, 'reporting');
      this.reportData = this.practiceLiveData;
      this.reportData?.moduleAssignmentsDetail.forEach(assignment => {
        assignment.visible = true;
        assignment.oldData = false;
      });
      this.toggleOldAssignments();
      this.setSortField(this.sortField);
      this.translationLoaded = true;
    })
  }

  isExpired(detail: any) {
    return detail.daysLeft <= 0;
  }

  getPositiveNumber(value: any): number {
    return Math.abs(value.daysLeft);
  }

  openModal(moduleAssignmentUuid: string, category: string, count: number, percentage: number, moduleAssignmentName: string): void {
    // Sending the Event of Clicking on a cell to access the User List to Amplitude
    this.amplitudeService.trackEvent('rollupreport_click_userlist', {
      'learner_list_status': category,
    });

    const selectedAccountInfo = localStorage.getItem('selected_account_info');
    const currentAccountUuid = localStorage.getItem('current_account_uuid');
    const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;

    this.dialog.open(LearnerListModalComponent, {
      hasBackdrop: true,
      data: {
        accountUuid: accountUuid,
        moduleAssignmentUuid: moduleAssignmentUuid,
        category: category,
        moduleAssignmentType: 'PRACTICE',
        count: count,
        percentage: percentage,
        moduleAssignmentName: moduleAssignmentName
      },
    });
  }

  toggleOldAssignments() {
    if (this.oldDataLoading) {
      return;
    }
    this.showOldAssignments = !this.showOldAssignments;
    if (!this.showOldAssignments) {
      this.ctaText = this.translocoService.translate('SEE_PRACTICE_ASSIGNMENT', {}, 'reporting');
      this.ctaImg = "assets/icons/arrow/chevron-up-dark-blue.svg";
    } else {
      this.ctaText = this.translocoService.translate('HIDE_OLDER_ASSIGNMENTS', {}, 'reporting');
      this.ctaImg = "/assets/icons/arrow/chevron-down-dark-blue.svg";
    }
    this.reportData?.moduleAssignmentsDetail.forEach(assignment => {
      if (assignment.oldData) {
        assignment.visible = this.showOldAssignments;
      }
    });

    this.remainingAssignments = (this.moduleAssignmentCount ?? 0) - (this.practiceLiveData?.moduleAssignmentsDetail?.length ?? 0);
    if (!this.showOldAssignments && this.remainingAssignments > 0) {
      this.toggleString = `${this.remainingAssignments} ${this.translocoService.translate('OLDER_PRACTICE', {}, 'reporting')} ${this.remainingAssignments === 1 ? `${this.translocoService.translate('ASSIGNMENT_IS', {}, 'reporting')}` : `${this.translocoService.translate('ASSIGNMENT_ARE', {}, 'reporting')}`} ${this.translocoService.translate('CURRENTLY_HIDDEN', {}, 'reporting')}.`;
    } else {
      this.toggleString = this.translocoService.translate('ALL_PRACTICE_ASSIGNMENTS', {}, 'reporting');
    }
  }

  fetchOldAssignments() {
    // Sending the Event of Clicking 'See all practice assignments' to Amplitude
    this.amplitudeService.trackEvent('rollupreport_click_expandpracticeassignments');

    if (!this.practiceOldData) {
      this.oldDataLoading = true;
      this.loadPracticeAssignments.emit();
    }

  }

  sortAssignments() {
    this.reportData?.moduleAssignmentsDetail.sort((a, b) => {
      const dateA = new Date(a.dueDate).getTime();
      const dateB = new Date(b.dueDate).getTime();
      return this.sortAscending ? dateA - dateB : dateB - dateA;
    });
  }

  setSortField(sortField: string) {
    this.sortField = sortField;
    this.toggleSortOrder();
  }

  toggleSortOrder() {
    this.sortAscending = !this.sortAscending;
    if (this.sortAscending) {
      this.sortIcon = '/assets/icons/arrow/arrow-narrow-down.svg';
    } else {
      this.sortIcon = '/assets/icons/arrow/arrow-narrow-up.svg';
    }
    this.sortList();
  }

  sortList() {
    console.log("calling sort list", this.sortField);
    if (!this.reportData?.moduleAssignmentsDetail || !this.sortField) return;

    const sortOrder = this.sortAscending; // true for ascending, false for descending
    const sortKey = this.sortField;

    const getNestedValue = (obj: any, key: string) => key.split('.').reduce((o, k) => (o ? o[k] : undefined), obj);

    this.reportData.moduleAssignmentsDetail.sort((a, b) => {
      const aValue = getNestedValue(a, sortKey);
      const bValue = getNestedValue(b, sortKey);

      // Handle null/undefined values: place them at the end
      if (aValue == null && bValue == null) return 0; // Both null, consider them equal
      if (aValue == null) return 1; // `a` is null, place it at the end
      if (bValue == null) return -1; // `b` is null, place it at the end

      if (sortKey === 'dueDate') {
        const aDate = new Date(aValue).getTime();
        const bDate = new Date(bValue).getTime();
        return sortOrder ? aDate - bDate : bDate - aDate;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder ? aValue - bValue : bValue - aValue;
      }

      return 0; // Default fallback for unsupported types
    });
  }



  navigateToRoute(moduleUuid: string) {
    const returnUrl = `/reporting_visualization?moduleUuid=${moduleUuid}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));
    this.router.navigate(['/reporting_visualization'], { queryParams: { moduleUuid: moduleUuid } });
  }

  openTooltip(event: MouseEvent, direction: string, content: string, width: number, height: number): void {
    let heading = '';
    let paragraph = '';
    if (content === 'notStarted') {
      paragraph = this.translocoService.translate('PRACTICE_NOT_STARTED_TOOLTIP', {}, 'reporting');
    } else if (content === 'inProgress') {
      paragraph = this.translocoService.translate('PRACTICE_IN_PROGRESS_TOOLTIP', {}, 'reporting');
    } else if (content === 'certified') {
      paragraph = this.translocoService.translate('PRACTICE_CERTIFIED_TOOLTIP', {}, 'reporting');
    } else if (content === 'userAssignmentCount') {
      paragraph = this.translocoService.translate('PRACTICE_USER_ASSIGNMENTCOUNT_TOOLTIP', {},  'reporting');
    } else if (content === 'avgPractice') {
      paragraph = this.translocoService.translate('PRACTICE_AVG_PRACTICE_TOOLTIP', {}, 'reporting');
    } else if (content === 'avgImprovement') {
      paragraph = this.translocoService.translate('PRACTICE_AVG_IMPROVEMENT_TOOLTIP', {}, 'reporting');
    }
    this.tooltipService.openTooltip(event, direction, heading, paragraph, width, height);
  }

}
